/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { DataTable } from '@carbon/react';
import { sortBy } from 'lodash';
import DOMPurify from 'dompurify';
import APIFactory from '../utils/api-factory';
import DefinitionDisplay from './DefinitionDisplay';
import { objectToArray, cleanUpKey } from '../utils/common';
import { getTranslatedExtensions } from '../utils/translate';

import Markdown from './Markdown';

const { TableContainer, Table, TableHead, TableHeader, TableBody, TableRow, TableExpandHeader, TableExpandRow, TableExpandedRow, TableCell } = DataTable;

const DefinitionsBody = ({ definitions, markupType, dummyPathName, dummyMethod, maxDepth }) => {
  const [expandedIndex, updateExpandedIndex] = useState(-1);
  const toggleRow = (rowIndex) => () => updateExpandedIndex(rowIndex === expandedIndex ? -1 : rowIndex);

  return (
    definitions &&
    definitions.length > 0 &&
    definitions.map((definition, index) => {
      const definitionName = definition.key;
      const definitionSchema = definition.value;
      const isExpanded = expandedIndex === index;
      return (
        <React.Fragment key={definitionName}>
          <TableExpandRow
            key={definitionName}
            className={classNames('definitionRow', `definition-${cleanUpKey(definitionName)}`)}
            isExpanded={isExpanded}
            ariaLabel="toggleCurrentRow"
            expandIconDescription="toggleCurrentRow"
            onExpand={toggleRow(index)}
            data-testid={`definitionRow-${index}`}
          >
            <TableCell className="definitionName">{definitionName}</TableCell>
            <TableCell className="definitionDescription">
              <Markdown data={getTranslatedExtensions(definitionSchema, 'description')} markupType={markupType} />
            </TableCell>
          </TableExpandRow>
          {isExpanded && (
            <TableExpandedRow className="expandrow" colSpan={12} data-testid={`definitionRow-expanded-${index}`}>
              <div className="tablecell">
                {(!dummyPathName || !dummyMethod) && <DefinitionDisplay object={{ schema: definitionSchema }} maxDepth={maxDepth} />}
                {dummyPathName && dummyMethod && (
                  <DefinitionDisplay object={{ schema: definitionSchema }} pathName={dummyPathName} method={dummyMethod} maxDepth={maxDepth} />
                )}
              </div>
            </TableExpandedRow>
          )}
        </React.Fragment>
      );
    })
  );
};

function Definitions({ api, infoSectionEntries, options }) {
  const { t } = useTranslation();
  let openApiHandler = APIFactory(api);

  // Assert the correct handler for the api
  if (api) {
    openApiHandler = openApiHandler.assertHandler(api);
  }
  let definitions = objectToArray(openApiHandler.getSchemasRoot(openApiHandler.getSchemasFullPath(api)));
  const markupType = openApiHandler.getMarkupType();
  definitions = sortBy(definitions, [(definition) => definition.key.toLowerCase()]);

  let maxDepth = 9;
  if (options && typeof options.apiMaxDepth !== 'undefined') {
    maxDepth = options.apiMaxDepth;
  }

  // Example generator uses "operation" only to determine whether to return JSON or XML. This can be overridden
  // so it actually makes no difference what we choose here. We just want to be able to generate an example.
  const dummyPathName = api.paths ? Object.keys(api.paths)[0] : null;
  const dummyMethod = dummyPathName ? Object.keys(api.paths[dummyPathName])[0] : null;
  const isSoap = openApiHandler.isSOAP(api);

  return (
    <div
      className={classNames(
        'apiconnect-explorer',
        ' definitions-section',
        'main',
        isSoap ? 'soap-api' : 'rest-api',
        `definitions-${cleanUpKey(`${api.info.title}_${api.info.version}`)}`
      )}
    >
      <section className="definitionsHeader" aria-label="definitions-header">
        <h2>{t('definitions')}</h2>
      </section>
      <section className="definitionsBody" aria-label="definitions-body">
        {infoSectionEntries &&
          infoSectionEntries.length > 0 &&
          infoSectionEntries.map((infoSectionEntry, index) => {
            if ((!infoSectionEntry.location || infoSectionEntry.location === 'DEFINITIONS') && infoSectionEntry.html) {
              return (
                <div
                  key={`infoSectionEntry_${infoSectionEntry + index}`}
                  className={classNames('infoSection', infoSectionEntry.class)}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(infoSectionEntry.html, {
                      ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'div'],
                      ALLOWED_ATTR: ['href', 'title', 'class', 'target', 'rel']
                    })
                  }}
                />
              );
            }
            return null;
          })}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableExpandHeader />
                <TableHeader>{t('definition')}</TableHeader>
                <TableHeader>{t('description')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              <DefinitionsBody definitions={definitions} markupType={markupType} dummyPathName={dummyPathName} dummyMethod={dummyMethod} maxDepth={maxDepth} />
            </TableBody>
          </Table>
        </TableContainer>
      </section>
    </div>
  );
}

Definitions.propTypes = {
  api: PropTypes.object
};

export default Definitions;
